import "../../../shared/styles/list.css";

import {
  Button,
  Col,
  Form,
  Modal,
  notification,
  Table,
  TablePaginationConfig,
  Dropdown,
  Typography,
  Space,
} from "antd";
import {
  ColumnsType,
  FilterValue,
  SorterResult,
} from "antd/lib/table/interface";
import { useEffect, useState } from "react";

import { PlusCircleTwoTone } from "@ant-design/icons";

import ContainerLayout from "../../common/containerLayout";
import {
  createCharter,
  deleteCharter,
  getPaginatedCharters,
  updateCharter,
} from "../../../services/charterApi";
import ComponentPlacesAutocomplete from "../../map/PlaceAutocomplete";
import { DispatcherMap } from "../../map/Map";
import { useAppSelector } from "../../../app/hooks";
import { AppDispatch, RootState } from "../../../app/store";
import { resetTrip, setOrigin } from "../../../features/map/mapSlice";
import { useDispatch } from "react-redux";
import { DirectionType } from "../../../shared/constants/DirectionType";
import { Country } from "../../../shared/dtos/country";
import dayjs from "dayjs";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}

export interface CharterDTO {
  id?: number;
  address?: string;
  latitude?: number;
  longitude?: number;
  countryName?: string;
  countryId?: number;
  country?: Country;
  charterTimes?: CharterTimeDTO[];
  newCharterTimes?: CharterTimeDTO[];
}

export interface CharterTimeDTO {
  id?: number;
  time?: string;
}

const { Text } = Typography;

const Charters = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [oldCharterTimes, setOldCharterTimes] = useState<CharterTimeDTO[]>([]);
  const { destinations, countryName } = useAppSelector(
    (state: RootState) => state?.map,
  );
  const dispatch = useDispatch<AppDispatch>();
  const allTimes = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? 0 : 30;
    return dayjs().hour(hour).minute(minute).second(0).format("HH:mm");
  });

  useEffect(() => {
    dispatch(resetTrip());
  }, [dispatch]);

  const actionItems = (record: CharterDTO) => {
    return [
      {
        key: "1",
        label: <Text>Actualizar</Text>,
        onClick: () => handleOpenModal(record),
      },
      {
        key: "2",
        label: <Text>Eliminar</Text>,
        onClick: () =>
          Modal.confirm({
            title: `Eliminar ${record.address}`,
            content: "Estas seguro de eliminar este charter?",
            onOk: async () => {
              await deleteCharter(record.id!);
              fetchData();
            },
            okText: "Confirmar",
            cancelText: "Cancelar",
          }),
      },
    ];
  };

  const columns: ColumnsType<CharterDTO> = [
    {
      title: "Direccion",
      dataIndex: "address",
      width: "20%",
    },
    {
      title: "Horarios",
      render: (record: CharterDTO) =>
        record.charterTimes
          ? record.charterTimes.map((x) => x.time).join(", ")
          : "Sin horario",
      width: "20%",
    },
    {
      title: "Acciones",
      key: "action",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: actionItems(record),
          }}
          placement="bottom"
          arrow
        >
          <Button>Acciones</Button>
        </Dropdown>
      ),
    },
  ];

  const handleOpenModal = (record?: CharterDTO) => {
    dispatch(resetTrip());
    if (record) {
      const savedAddress = {
        label: record.address,
      };

      if (record.charterTimes) {
        setOldCharterTimes(record.charterTimes);
        const parsed = record.charterTimes.map((charterTime) =>
          dayjs(charterTime.time, "HH:mm:ss").format("HH:mm"),
        );
        setSelectedTimes(parsed);
      }

      form.setFieldsValue({
        id: record.id,
        address: savedAddress,
      });
      dispatch(
        setOrigin(
          {
            id: "",
            originAddress: record.address!,
            originLatitude: +record.latitude!,
            originLongitude: +record.longitude!,
          },
          record.country?.name!,
        ),
      );
    }
    setOpenModal(true);
  };

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {},
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const fetchData = async () => {
    setLoading(true);

    const { data, total } = await getPaginatedCharters({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
    });

    setData(data);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CharterDTO> | SorterResult<CharterDTO>[],
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const handleClose = () => {
    setOldCharterTimes([]);
    setSelectedTimes([]);
    form.resetFields();
    setOpenModal(false);
  };

  const handleOk = async () => {
    const id = form.getFieldValue("id");
    const origin = destinations?.pickUp;

    if (!origin) return;

    const charter: CharterDTO = {
      address: origin!.originAddress,
      latitude: origin!.originLatitude,
      longitude: origin!.originLongitude,
      countryName,
    };

    if (!id)
      charter.charterTimes = selectedTimes.map((t) => ({
        time: dayjs(t, "HH:mm").format("HH:mm:ss"),
      }));

    if (id && selectedTimes.length) {
      charter.newCharterTimes = selectedTimes.map((t) => ({
        time: dayjs(t, "HH:mm").format("HH:mm:ss"),
      }));
      charter.charterTimes = oldCharterTimes;
    }

    try {
      if (id) await updateCharter(id, charter);
      else await createCharter(charter);
      fetchData();
      handleClose();
    } catch {
      notification.error({
        message: `Ha ocurrido un error al ${!id ? "crear" : "editar"} charter.`,
        key: "global",
      });
    }
  };

  const toggleTime = (time: string) => {
    setSelectedTimes((prev) =>
      prev.includes(time) ? prev.filter((t) => t !== time) : [...prev, time],
    );
  };

  const handleSelectAll = () => setSelectedTimes(allTimes);
  const handleClearAll = () => setSelectedTimes([]);

  return (
    <ContainerLayout title="Shuttles" showNotification={false}>
      <Col span={24}>
        <Button
          onClick={() => handleOpenModal()}
          icon={<PlusCircleTwoTone />}
          type="primary"
        >
          Shuttle
        </Button>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          columns={columns}
          locale={{ emptyText: "No hay datos" }}
          rowKey={(record) => record.id!}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: "calc(-250px + 99vh)" }}
        />
        <Modal
          title={`${!form.getFieldValue("id") ? "Crear" : "Editar"} shuttle`}
          open={openModal}
          onOk={form.submit}
          onCancel={handleClose}
          forceRender={true}
        >
          <Form
            form={form}
            onFinish={handleOk}
            layout="vertical"
            name="upsertCharter"
          >
            <Form.Item label="Seleccione horarios" name="time">
              {allTimes.map((time) => (
                <Button
                  key={time}
                  type={selectedTimes.includes(time) ? "primary" : "default"}
                  onClick={() => toggleTime(time)}
                  style={{ margin: "4px" }}
                >
                  {time}
                </Button>
              ))}
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" onClick={handleSelectAll}>
                  Seleccionar todos
                </Button>
                <Button onClick={handleClearAll}>Limpiar</Button>
              </Space>
            </Form.Item>
            <Form.Item
              label="Direccion"
              name="address"
              valuePropName="placeholder"
            >
              <ComponentPlacesAutocomplete type={DirectionType.Charter} />
            </Form.Item>
          </Form>
          {openModal && (
            <DispatcherMap destinations={destinations} height="30vh" />
          )}
        </Modal>
      </Col>
    </ContainerLayout>
  );
};

export default Charters;
